"use client";

import { useEffect, useId, useRef, type ComponentPropsWithoutRef, type MouseEvent, type ReactNode, type SyntheticEvent } from "react";

import { cx } from "../../lib/cx";
import { IconButton } from "../core/IconButton";
import s from "./Dialog.module.css";

export type DialogSize = "sm" | "md" | "lg";

export interface DialogProps extends Omit<ComponentPropsWithoutRef<"dialog">, "title" | "open" | "onClose"> {
  /** Cerrado no renderiza nada, como antes: el contenido puede depender de lo que se está editando. */
  open?: boolean;
  title?: string;
  /** Una segunda línea pegada al título, más chica y gris (el email de la persona que se gestiona). */
  subtitle?: string;
  /** Una línea de explicación debajo del título. */
  description?: string;
  /** Algo antes del título, a su izquierda: típicamente un `Avatar`. */
  leading?: ReactNode;
  children?: ReactNode;
  /** La fila de acciones, alineada a la derecha: típicamente dos `Button`. */
  footer?: ReactNode;
  /** Se llama con Escape y con click en el fondo. Sin `onClose`, el diálogo no se cierra desde afuera. */
  onClose?: () => void;
  size?: DialogSize;
  /** API vieja: un ancho en px. Se mapea al `size` más cercano. */
  width?: number;
}

function tamanoDesdeAncho(width?: number): DialogSize | undefined {
  if (width === undefined) return undefined;
  if (width <= 400) return "sm";
  if (width <= 500) return "md";
  return "lg";
}

/** Modal centrado sobre el scrim. Es un `<dialog>` nativo: top layer, foco atrapado y Escape vienen del browser. */
export function Dialog({
  open = true,
  title,
  subtitle,
  description,
  leading,
  children,
  footer,
  onClose,
  size,
  width,
  className,
  ...rest
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const idTitulo = useId();
  const idDescripcion = useId();
  const tamano = size ?? tamanoDesdeAncho(width) ?? "md";

  // Abierto = `showModal()`: pone el diálogo en el top layer y le da el foco
  // al primer control; al desmontar, el browser lo cierra solo. Depende de
  // `open` y no de `[]` porque el diálogo puede montarse cerrado y abrirse
  // después con la prop (Equipo, Carteles). El fondo queda inerte, pero no
  // deja de desplazarse: eso se corta a mano en el body mientras dure.
  useEffect(() => {
    const el = ref.current;
    if (!open || !el || el.open) return;
    el.showModal();
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflowPrevio;
    };
  }, [open]);

  if (!open) return null;

  // Escape dispara `cancel`. Sin `preventDefault` el browser cierra el <dialog>
  // pero React seguiría creyendo que está abierto; se corta acá y se avisa.
  const alCancelar = (e: SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    onClose?.();
  };
  // El <dialog> no tiene padding: un click cuyo target es el propio elemento
  // sólo puede haber caído en el fondo.
  const alClickear = (e: MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <dialog
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? idTitulo : undefined}
      aria-describedby={description ? idDescripcion : undefined}
      className={cx(s.dialog, className)}
      data-size={tamano}
      onCancel={alCancelar}
      onClick={alClickear}
      {...rest}
    >
      <div className={s.panel}>
        <div className={s.cabecera}>
          <div className={s.textos}>
            <div className={s.encabezado}>
              {leading}
              <div className={s.titulos}>
                {title ? (
                  <h2 id={idTitulo} className={s.titulo}>
                    {title}
                  </h2>
                ) : null}
                {subtitle ? <span className={s.subtitulo}>{subtitle}</span> : null}
              </div>
            </div>
            {description ? (
              <p id={idDescripcion} className={s.descripcion}>
                {description}
              </p>
            ) : null}
          </div>
          {onClose ? <IconButton icon="x" label="Cerrar" variant="soft" size="sm" onClick={onClose} /> : null}
        </div>
        {children ? <div className={s.cuerpo}>{children}</div> : null}
        {footer ? <div className={s.pie}>{footer}</div> : null}
      </div>
    </dialog>
  );
}
